import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Tables } from '../../../database.types';
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ArrowLeft, TrendingUp, Target, Info, ShoppingBag, Wrench, Facebook, Zap, Sparkles, ChevronRight, ShieldAlert, Clipboard, Check, Lightbulb, MessageSquare, Flame, AlertTriangle, ExternalLink, Calendar, Plus } from "lucide-react"
import { Separator } from "@/components/ui/separator"
import { useRole } from "@/lib/useRole"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useToast } from "@/components/ui/use-toast"
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer } from 'recharts';
import { Skeleton } from "@/components/ui/skeleton";
import ContentGallery from '@/components/content/ContentGallery';
import convergenceBackgroundImage from '@/assets/images/convergence_background.png';

type Convergence = Tables<'convergences'>;
type NewsContent = Tables<'news_contents'>;

// Add this interface to properly type the joined query result
interface ConvergenceWithBrand extends Convergence {
    brands: {
        id: string;
        brand_name: string;
    };
    news_contents?: NewsContent;
}


// Add interface for metadata
interface ConvergenceMetadata {
    ideas?: {
        unique_insights?: string;
        brand_opinion?: string;
        hot_takes?: string;
        controversial_takes?: string;
    };
}

// Define the structure for interesting links
interface InterestingLink {
    url: string;
    image?: string;
    title: string;
    author?: string;
    contents: string;
    publishedDate?: string;
}

// Helper function to safely access metadata
const getMetadata = (metadata: any): ConvergenceMetadata | null => {
    if (!metadata || typeof metadata !== 'object') return null;
    return metadata as ConvergenceMetadata;
};

// Helper function to parse interesting links
const parseInterestingLinks = (interestingLinks: any): InterestingLink[] => {
    try {
        if (!interestingLinks) return [];
        if (Array.isArray(interestingLinks)) {
            return interestingLinks.filter((item: any) => item && typeof item === 'object' && 'url' in item) as InterestingLink[];
        }
        return [];
    } catch (e) {
        console.error('Error parsing interesting links:', e);
        return [];
    }
};

export const ConvergenceDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [convergence, setConvergence] = useState<ConvergenceWithBrand | null>(null);
    const [isNewsContentExpanded, setIsNewsContentExpanded] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [relatedContent, setRelatedContent] = useState<any[]>([]);
    const [contentLoading, setContentLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [statusLoading, setStatusLoading] = useState(false);
    const navigate = useNavigate();
    const { isAdmin } = useRole();
    const { toast } = useToast();

    // Valid status values from database enum
    const validStatuses: Tables<'convergences'>['status'][] = ['Used', 'Unused', 'Unranked', 'Archived'];

    // Get metadata safely
    const metadata = convergence ? getMetadata(convergence.metadata) : null;

    useEffect(() => {
        fetchConvergenceDetails();
        fetchRelatedContent();
    }, [id]);

    const fetchConvergenceDetails = async () => {
        if (!id) return;

        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('convergences')
                .select(`
                    *,
                    brands!inner (
                        id,
                        brand_name
                    ),
                    news_contents (
                        id,
                        name,
                        description,
                        trend_analysis,
                        primary_keyword,
                        months_till_peak,
                        total_duration_in_months,
                        virality_score,
                        virality_explanation,
                        comparable_trends,
                        business_opportunities_analysis,
                        audience_and_brand_analysis,
                        raw_headline,
                        raw_content,
                        longevity_in_days,
                        authoritative_urls,
                        interesting_links
                    )
                `)
                .eq('id', id)
                .single();

            if (error) throw error;

            if (data) {
                setConvergence(data as unknown as ConvergenceWithBrand);
            }
        } catch (error) {
            console.error('Error fetching convergence details:', error);
            toast({
                title: "Error",
                description: "Failed to load convergence details",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchRelatedContent = async () => {
        if (!id) return;

        try {
            setContentLoading(true);
            // Query content_management_view directly for content with this convergence_id
            const { data: contents, error: contentError } = await supabase
                .from('content_management_view')
                .select('*')
                .eq('convergence_id', id)
                .order('created_at', { ascending: false });

            if (contentError) throw contentError;

            // The content_management_view already has all the fields we need
            // including content_json, name, campaign info, etc.
            setRelatedContent(contents || []);
        } catch (error) {
            console.error('Error fetching related content:', error);
        } finally {
            setContentLoading(false);
        }
    };

    const handleStatusChange = async (newStatus: Tables<'convergences'>['status']) => {
        if (!convergence || !id || statusLoading) return;
        
        try {
            setStatusLoading(true);
            
            const { error } = await supabase
                .from('convergences')
                .update({ status: newStatus })
                .eq('id', id);

            if (error) throw error;

            // Update local state
            setConvergence(prev => prev ? { ...prev, status: newStatus } : null);
            
            toast({
                title: "Status Updated",
                description: `Convergence status changed to ${newStatus}`,
            });
        } catch (error) {
            console.error('Error updating convergence status:', error);
            toast({
                title: "Error",
                description: "Failed to update convergence status",
                variant: "destructive",
            });
        } finally {
            setStatusLoading(false);
        }
    };

    const formatRawData = (data: any) => {
        return Object.entries(data).map(([key, value]) => (
            `${key}:\n${typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}\n\n`
        )).join('');
    };

    const copyToClipboard = async () => {
        if (!convergence) return;
        
        try {
            const formattedData = formatRawData(convergence);
            await navigator.clipboard.writeText(formattedData);
            setIsCopied(true);
            toast({
                title: "Copied to clipboard",
                description: "Raw convergence data has been copied to clipboard",
            });
            
            // Reset copy state after 2 seconds
            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
            toast({
                title: "Failed to copy",
                description: "Could not copy data to clipboard",
                variant: "destructive",
            });
        }
    };


    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                <div className="relative container mx-auto p-6 max-w-7xl space-y-6">
                    <Skeleton className="h-10 w-32" />
                    <Skeleton className="h-12 w-64" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Skeleton className="h-64 rounded-xl" />
                        <Skeleton className="h-64 rounded-xl" />
                    </div>
                    <Skeleton className="h-96 rounded-xl" />
                </div>
            </div>
        );
    }

    if (!convergence) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                <div className="relative container mx-auto p-6 max-w-7xl">
                    <Card className="bg-white/60 backdrop-blur-sm border-white/50 shadow-sm">
                        <div className="p-8 text-center">
                            <h2 className="text-lg font-semibold mb-2">Convergence Not Found</h2>
                            <p className="text-muted-foreground">The convergence you're looking for doesn't exist.</p>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="relative container mx-auto p-6 max-w-7xl space-y-8">
                {/* Enhanced Header */}
                <div className="flex items-center justify-between">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/convergences')}
                        className="group hover:bg-white/70"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
                        Back to Convergences
                    </Button>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-muted-foreground">Status:</span>
                            <Select
                                value={convergence.status}
                                onValueChange={handleStatusChange}
                                disabled={statusLoading}
                            >
                                <SelectTrigger className="w-32 h-8 text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {validStatuses.map((status) => (
                                        <SelectItem key={status} value={status} className="text-xs">
                                            {status}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Badge
                            variant={convergence.status === 'Used' ? 'secondary' : 'default'}
                            className="shadow-sm"
                        >
                            {convergence.status}
                        </Badge>
                    </div>
                </div>

                {/* Enhanced Hero Section */}
                <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    
                    {/* Hero Image Section - Always show, with fallback */}
                    <div className="relative h-64 lg:h-80 overflow-hidden">
                        <img
                            src={(convergence as any).image_url || convergenceBackgroundImage}
                            alt={convergence.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                // Fallback to default background if image fails to load
                                e.currentTarget.src = convergenceBackgroundImage;
                            }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
                            
                        
                        {/* Title overlay on image */}
                        <div className="absolute bottom-0 left-0 right-0 p-8">
                            <div className="bg-black/70 p-6 rounded-2xl backdrop-blur-md">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg">
                                        <Sparkles className="h-8 w-8 text-blue-600" />
                                    </div>
                                    <div>
                                        <h1 className="text-4xl font-bold text-white mb-2" style={{ color: 'white' }}>
                                            {convergence.name}
                                        </h1>
                                        <div className="flex items-center gap-4 text-sm">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="h-4 w-4" style={{ color: 'white' }} />
                                                <span className="font-medium" style={{ color: 'white' }}>
                                                    {new Date(convergence.created_at).toLocaleDateString('en-US', { 
                                                        month: 'short', 
                                                        day: 'numeric', 
                                                        year: 'numeric' 
                                                    })}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Target className="h-4 w-4" style={{ color: 'white' }} />
                                                <span className="font-medium" style={{ color: 'white' }}>{convergence.brands.brand_name}</span>
                                            </div>
                                            {convergence.expiration_date && (
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="h-4 w-4" style={{ color: 'white' }} />
                                                    <span className="font-medium" style={{ color: 'white' }}>
                                                        Expires: {new Date(convergence.expiration_date).toLocaleDateString('en-US', { 
                                                            month: 'short', 
                                                            day: 'numeric', 
                                                            year: 'numeric' 
                                                        })}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            {/* Power Score and Related Campaigns Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {convergence.power_score && (
                    <Card className="bg-white rounded-lg border shadow-sm overflow-hidden">
                        <div className="p-4 border-b bg-gradient-to-r from-amber-50 to-orange-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                                    <Zap className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-amber-900">Power Score</h2>
                                    <p className="text-sm text-amber-700">Overall convergence strength</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                                <div className="flex flex-col md:flex-row gap-4 items-center">
                                    <div className="relative w-32 h-32">
                                        <svg className="w-full h-full transform -rotate-90">
                                            <circle
                                                cx="64"
                                                cy="64"
                                                r="56"
                                                fill="none"
                                                stroke="#E5E7EB"
                                                strokeWidth="10"
                                                className="opacity-25"
                                            />
                                            <circle
                                                cx="64"
                                                cy="64"
                                                r="56"
                                                fill="none"
                                                stroke="url(#powerGradient)"
                                                strokeWidth="10"
                                                strokeLinecap="round"
                                                strokeDasharray={`${(convergence.power_score || 0) * 3.52} 352`}
                                            />
                                            <defs>
                                                <linearGradient id="powerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                    <stop offset="0%" stopColor="#F59E0B" />
                                                    <stop offset="100%" stopColor="#DC2626" />
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-4xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                                {convergence.power_score}
                                            </span>
                                        </div>
                                    </div>
                            {(convergence.virality_score || convergence.relevance_score || 
                              convergence.interest_to_audience_score || convergence.crossover_potential_score) && (
                                <div className="flex-1 h-64 w-full">
                                    <h3 className="text-sm font-medium text-center text-gray-600 mb-2">Score Breakdown</h3>
                                    <ResponsiveContainer width="100%" height="90%">
                                        <RadarChart 
                                            cx="50%" 
                                            cy="50%" 
                                            outerRadius="80%" 
                                            data={[
                                                {
                                                    subject: 'Virality',
                                                    score: convergence.virality_score || 0,
                                                    fullMark: 10
                                                },
                                                {
                                                    subject: 'Relevance',
                                                    score: convergence.relevance_score || 0,
                                                    fullMark: 10
                                                },
                                                {
                                                    subject: 'Interest',
                                                    score: convergence.interest_to_audience_score || 0,
                                                    fullMark: 10
                                                },
                                                {
                                                    subject: 'Crossover',
                                                    score: convergence.crossover_potential_score || 0,
                                                    fullMark: 10
                                                }
                                            ].filter(item => 
                                                // Only include scores that exist in the dataset
                                                (convergence.virality_score && item.subject === 'Virality') ||
                                                (convergence.relevance_score && item.subject === 'Relevance') ||
                                                (convergence.interest_to_audience_score && item.subject === 'Interest') ||
                                                (convergence.crossover_potential_score && item.subject === 'Crossover')
                                            )}
                                        >
                                            <PolarGrid stroke="#e5e7eb" />
                                            <PolarAngleAxis 
                                                dataKey="subject" 
                                                tick={{ fill: '#6b7280', fontSize: 12 }}
                                            />
                                            <PolarRadiusAxis 
                                                angle={30} 
                                                domain={[0, 10]} 
                                                tick={{ fill: '#6b7280', fontSize: 10 }}
                                            />
                                            <Radar
                                                name="Score"
                                                dataKey="score"
                                                stroke="#F59E0B"
                                                fill="#F59E0B"
                                                fillOpacity={0.6}
                                                dot={{ fill: '#F59E0B', strokeWidth: 0 }}
                                            />
                                            <Legend 
                                                wrapperStyle={{ fontSize: 12, marginTop: '10px' }}
                                            />
                                        </RadarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                        </div>
                        <div className="h-2 bg-gradient-to-r from-amber-200 via-orange-200 to-red-200" />
                    </Card>
                )}

                <Card className="bg-white rounded-lg border shadow-sm overflow-hidden">
                    <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                                    <Target className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-blue-900">Related Content</h2>
                                    <p className="text-sm text-blue-700">Content using this convergence</p>
                                </div>
                            </div>
                            <Button
                                onClick={() => navigate(`/content/create?convergenceId=${convergence.id}&brandId=${convergence.brands.id}`)}
                                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm sm:ml-auto"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Create Content
                            </Button>
                        </div>
                    </div>
                    <div className="p-6">
                        <ContentGallery
                            contents={relatedContent}
                            loading={contentLoading}
                            emptyMessage="No content has been created for this convergence yet"
                            onContentClick={(content) => navigate(`/content/${content.id}`)}
                            showCampaign={true}
                        />
                    </div>
                    <div className="h-2 bg-gradient-to-r from-blue-200 via-indigo-200 to-purple-200" />
                </Card>
            </div>


            {/* Trend Information */}
            <Card className="bg-white rounded-lg border shadow-sm overflow-hidden mb-6">
                <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-violet-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center shadow-lg">
                            <TrendingUp className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-purple-900">Trend Information</h2>
                            <p className="text-sm text-purple-700">Key trend details and analysis</p>
                        </div>
                    </div>
                </div>
                <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="font-medium mb-2">Trend</h3>
                        <p>{convergence.trend}</p>
                    </div>
                    <div>
                        <h3 className="font-medium mb-2">Trend Description</h3>
                        <p>{convergence.trend_description}</p>
                    </div>
                </div>

                {convergence.news_contents && (
                    <div className="mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setIsNewsContentExpanded(!isNewsContentExpanded)}
                            className="w-full justify-between"
                        >
                            <span>Related News Content Details</span>
                            <ChevronRight className={`h-4 w-4 transform transition-transform ${isNewsContentExpanded ? 'rotate-90' : ''}`} />
                        </Button>
                        
                        {isNewsContentExpanded && (
                            <div className="mt-4 space-y-4">
                                {/* Authoritative URLs */}
                                {convergence.news_contents.authoritative_urls && (
                                    <div>
                                        <h3 className="font-medium mb-2">Authoritative Sources</h3>
                                        <div className="space-y-2">
                                            {Array.isArray(convergence.news_contents.authoritative_urls) && 
                                                convergence.news_contents.authoritative_urls.map((item: any, index: number) => (
                                                    <div key={index} className="flex items-center">
                                                        <ExternalLink className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                                                        <a 
                                                            href={item.url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            className="text-sm text-blue-500 hover:underline truncate"
                                                        >
                                                            {item.title || item.url}
                                                        </a>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                )}

                                {/* Interesting Links */}
                                {(() => {
                                    const interestingLinks = parseInterestingLinks(convergence.news_contents.interesting_links);
                                    return interestingLinks.length > 0 && (
                                        <div>
                                            <h3 className="font-medium mb-3">Interesting Links</h3>
                                            <div className="space-y-4">
                                                {interestingLinks.map((item, index) => (
                                                    <div key={index} className="bg-muted/30 p-4 rounded-lg border">
                                                        <div className="flex gap-3">
                                                            {/* Image column */}
                                                            {item.image && (
                                                                <div className="flex-shrink-0 w-20 h-20">
                                                                    <img 
                                                                        src={item.image} 
                                                                        alt={item.title || 'Content image'} 
                                                                        className="w-full h-full object-cover rounded border"
                                                                        onError={(e) => {
                                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                            
                                                            {/* Content column */}
                                                            <div className="flex-1 min-w-0">
                                                                {item.title && (
                                                                    <h4 className="font-medium text-sm mb-1 text-foreground line-clamp-2">
                                                                        {item.title}
                                                                    </h4>
                                                                )}
                                                                
                                                                <div className="flex items-center gap-1 mb-2">
                                                                    <ExternalLink className="h-3 w-3 text-blue-500 flex-shrink-0" />
                                                                    <a 
                                                                        href={item.url} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer" 
                                                                        className="text-xs text-blue-500 hover:underline truncate"
                                                                    >
                                                                        {item.url}
                                                                    </a>
                                                                </div>
                                                                
                                                                <div className="flex gap-2 text-xs text-muted-foreground mb-2">
                                                                    {item.author && (
                                                                        <span>By {item.author}</span>
                                                                    )}
                                                                    {item.publishedDate && (
                                                                        <span>• {new Date(item.publishedDate).toLocaleDateString()}</span>
                                                                    )}
                                                                </div>
                                                                
                                                                {item.contents && (
                                                                    <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                                                                        {item.contents}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                                
                                {convergence.news_contents.raw_headline && (
                                    <div>
                                        <h3 className="font-medium mb-2">Original Headline</h3>
                                        <p className="text-sm">{convergence.news_contents.raw_headline}</p>
                                    </div>
                                )}

                                {convergence.news_contents.raw_content && (
                                    <div>
                                        <h3 className="font-medium mb-2">Raw Content</h3>
                                        <p className="text-sm">{convergence.news_contents.raw_content}</p>
                                    </div>
                                )}
                                
                                {convergence.news_contents.description && (
                                    <div>
                                        <h3 className="font-medium mb-2">Description</h3>
                                        <p className="text-sm">{convergence.news_contents.description}</p>
                                    </div>
                                )}

                                {(convergence.news_contents as any).contents && (
                                    <div>
                                        <h3 className="font-medium mb-2">Contents</h3>
                                        <p className="text-sm whitespace-pre-wrap">{(convergence.news_contents as any).contents}</p>
                                    </div>
                                )}

                                {(convergence.news_contents as any).images && (
                                    <div>
                                        <h3 className="font-medium mb-2">Images</h3>
                                        <div className="space-y-2">
                                            {(() => {
                                                try {
                                                    const images = Array.isArray((convergence.news_contents as any).images) 
                                                        ? (convergence.news_contents as any).images 
                                                        : [];
                                                    return images.map((imageUrl: any, index: number) => (
                                                        <div key={index} className="border rounded p-2">
                                                            <img 
                                                                src={typeof imageUrl === 'string' ? imageUrl : imageUrl?.url || imageUrl} 
                                                                alt={`News content image ${index + 1}`}
                                                                className="max-w-full h-auto rounded"
                                                                onError={(e) => {
                                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                                }}
                                                            />
                                                        </div>
                                                    ));
                                                } catch (e) {
                                                    return <p className="text-sm text-muted-foreground">Error loading images</p>;
                                                }
                                            })()}
                                        </div>
                                    </div>
                                )}
                                
                                {convergence.news_contents.trend_analysis && (
                                    <div>
                                        <h3 className="font-medium mb-2">Trend Analysis</h3>
                                        <p className="text-sm">{convergence.news_contents.trend_analysis}</p>
                                    </div>
                                )}
                                
                                {convergence.news_contents.business_opportunities_analysis && (
                                    <div>
                                        <h3 className="font-medium mb-2">Business Opportunities</h3>
                                        <p className="text-sm">{convergence.news_contents.business_opportunities_analysis}</p>
                                    </div>
                                )}
                                
                                {convergence.news_contents.audience_and_brand_analysis && (
                                    <div>
                                        <h3 className="font-medium mb-2">Audience & Brand Analysis</h3>
                                        <p className="text-sm">{convergence.news_contents.audience_and_brand_analysis}</p>
                                    </div>
                                )}
                                
                                <div className="grid grid-cols-2 gap-4">
                                    {convergence.news_contents.months_till_peak && (
                                        <div>
                                            <h3 className="font-medium mb-2">Months Till Peak</h3>
                                            <p className="text-sm">{convergence.news_contents.months_till_peak}</p>
                                        </div>
                                    )}
                                    
                                    {convergence.news_contents.total_duration_in_months && (
                                        <div>
                                            <h3 className="font-medium mb-2">Total Duration (Months)</h3>
                                            <p className="text-sm">{convergence.news_contents.total_duration_in_months}</p>
                                        </div>
                                    )}
                                    
                                    {convergence.news_contents.longevity_in_days && (
                                        <div>
                                            <h3 className="font-medium mb-2">Longevity (Days)</h3>
                                            <p className="text-sm">{convergence.news_contents.longevity_in_days}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                </div>
                <div className="h-2 bg-gradient-to-r from-purple-200 via-violet-200 to-indigo-200" />
            </Card>

            {/* Convergence Details */}
            <Card className="bg-white rounded-lg border shadow-sm overflow-hidden mb-6">
                <div className="p-4 border-b bg-gradient-to-r from-cyan-50 to-teal-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                            <Info className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-cyan-900">Convergence Details</h2>
                            <p className="text-sm text-cyan-700">In-depth convergence information</p>
                        </div>
                    </div>
                </div>
                <div className="p-6">
                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium mb-2">Convergence</h3>
                        <p className="whitespace-pre-wrap">{convergence.convergence}</p>
                    </div>
                    <Separator className="my-4" />
                    
                    {/* Metadata Fields */}
                    {metadata?.ideas && (
                        <>
                            <div className="space-y-4">
                                {/* Unique Insights */}
                                {metadata.ideas.unique_insights && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Lightbulb className="h-5 w-5 text-amber-500" />
                                            <h3 className="font-medium">Unique Insights</h3>
                                        </div>
                                        <p className="whitespace-pre-wrap pl-7">{metadata.ideas.unique_insights}</p>
                                    </div>
                                )}
                                
                                {/* Brand Opinion */}
                                {metadata.ideas.brand_opinion && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <MessageSquare className="h-5 w-5 text-blue-500" />
                                            <h3 className="font-medium">Brand Opinion</h3>
                                        </div>
                                        <p className="whitespace-pre-wrap pl-7">{metadata.ideas.brand_opinion}</p>
                                    </div>
                                )}
                                
                                {/* Hot Takes */}
                                {metadata.ideas.hot_takes && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Flame className="h-5 w-5 text-orange-500" />
                                            <h3 className="font-medium">Hot Takes</h3>
                                        </div>
                                        <p className="whitespace-pre-wrap pl-7">{metadata.ideas.hot_takes}</p>
                                    </div>
                                )}
                                
                                {/* Controversial Takes */}
                                {metadata.ideas.controversial_takes && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <AlertTriangle className="h-5 w-5 text-red-500" />
                                            <h3 className="font-medium">Controversial Takes</h3>
                                        </div>
                                        <p className="whitespace-pre-wrap pl-7">{metadata.ideas.controversial_takes}</p>
                                    </div>
                                )}
                                
                                {/* Promotional Idea */}
                                {convergence.promo_idea && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Zap className="h-5 w-5 text-yellow-500" />
                                            <h3 className="font-medium">Promotional Idea</h3>
                                        </div>
                                        <p className="whitespace-pre-wrap pl-7">{convergence.promo_idea}</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
                </div>
                <div className="h-2 bg-gradient-to-r from-cyan-200 via-teal-200 to-emerald-200" />
            </Card>

            {/* Related News and Data */}
            {convergence.related_news_and_data && (
                <Card className="bg-white rounded-lg border shadow-sm overflow-hidden mb-6">
                    <div className="p-4 border-b bg-gradient-to-r from-red-50 to-rose-50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-600 rounded-full flex items-center justify-center shadow-lg">
                                <Target className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-red-900">Related News and Data</h2>
                                <p className="text-sm text-red-700">Supporting information and context</p>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        <p className="whitespace-pre-wrap">{convergence.related_news_and_data}</p>
                    </div>
                    <div className="h-2 bg-gradient-to-r from-red-200 via-rose-200 to-pink-200" />
                </Card>
            )}

            {/* All Scores */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {convergence.virality_score && (
                    <Card className="bg-white rounded-lg border shadow-sm overflow-hidden">
                        <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-violet-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center">
                                        <TrendingUp className="h-4 w-4 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-purple-900">Virality Score</h3>
                                </div>
                                <span className="text-2xl font-bold text-purple-600">
                                    {convergence.virality_score}
                                </span>
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="flex flex-col">
                            {convergence.virality_explanation && (
                                <p className="text-sm text-muted-foreground mb-2">
                                    {convergence.virality_explanation}
                                </p>
                            )}
                            {convergence.virality_score_explanation && (
                                <p className="text-sm text-muted-foreground">
                                    {convergence.virality_score_explanation}
                                </p>
                            )}
                            </div>
                        </div>
                        <div className="h-1 bg-gradient-to-r from-purple-200 to-violet-200" />
                    </Card>
                )}

                {convergence.relevance_score && (
                    <Card className="bg-white rounded-lg border shadow-sm overflow-hidden">
                        <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-cyan-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center">
                                        <Target className="h-4 w-4 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-blue-900">Relevance Score</h3>
                                </div>
                                <span className="text-2xl font-bold text-blue-600">
                                    {convergence.relevance_score}
                                </span>
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="flex flex-col">
                            {convergence.relevance_explanation && (
                                <p className="text-sm text-muted-foreground mb-2">
                                    {convergence.relevance_explanation}
                                </p>
                            )}
                            {convergence.relevance_score_explanation && (
                                <p className="text-sm text-muted-foreground">
                                    {convergence.relevance_score_explanation}
                                </p>
                            )}
                            </div>
                        </div>
                        <div className="h-1 bg-gradient-to-r from-blue-200 to-cyan-200" />
                    </Card>
                )}

                {convergence.interest_to_audience_score && (
                    <Card className="bg-white rounded-lg border shadow-sm overflow-hidden">
                        <div className="p-4 border-b bg-gradient-to-r from-green-50 to-emerald-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                                        <Lightbulb className="h-4 w-4 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-green-900">Interest Score</h3>
                                </div>
                                <span className="text-2xl font-bold text-green-600">
                                    {convergence.interest_to_audience_score}
                                </span>
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="flex flex-col">
                            {convergence.interest_to_audience_explanation && (
                                <p className="text-sm text-muted-foreground">
                                    {convergence.interest_to_audience_explanation}
                                </p>
                            )}
                            </div>
                        </div>
                        <div className="h-1 bg-gradient-to-r from-green-200 to-emerald-200" />
                    </Card>
                )}

                {convergence.crossover_potential_score && (
                    <Card className="bg-white rounded-lg border shadow-sm overflow-hidden">
                        <div className="p-4 border-b bg-gradient-to-r from-orange-50 to-amber-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-600 rounded-full flex items-center justify-center">
                                        <Zap className="h-4 w-4 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-orange-900">Crossover Score</h3>
                                </div>
                                <span className="text-2xl font-bold text-orange-600">
                                    {convergence.crossover_potential_score}
                                </span>
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="flex flex-col">
                            {convergence.crossover_potential_explanation && (
                                <p className="text-sm text-muted-foreground">
                                    {convergence.crossover_potential_explanation}
                                </p>
                            )}
                            </div>
                        </div>
                        <div className="h-1 bg-gradient-to-r from-orange-200 to-amber-200" />
                    </Card>
                )}
            </div>

                {/* Opportunities and Tools */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {convergence.quick_products && (
                        <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm">
                            <div className="absolute inset-0 bg-gradient-to-r from-green-50/30 via-transparent to-emerald-50/30" />
                            <div className="relative p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="p-2 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg">
                                        <ShoppingBag className="h-5 w-5 text-green-600" />
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-900">Quick Products</h2>
                                </div>
                                <pre className="whitespace-pre-wrap text-sm bg-white/40 p-4 rounded-lg border border-white/50">
                                    {JSON.stringify(convergence.quick_products, null, 2)}
                                </pre>
                            </div>
                        </div>
                )}

                    {convergence.digital_tools && (
                        <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm">
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-50/30 via-transparent to-indigo-50/30" />
                            <div className="relative p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="p-2 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg">
                                        <Wrench className="h-5 w-5 text-purple-600" />
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-900">Digital Tools</h2>
                                </div>
                                <pre className="whitespace-pre-wrap text-sm bg-white/40 p-4 rounded-lg border border-white/50">
                                    {JSON.stringify(convergence.digital_tools, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                {/* Facebook Targeting */}
                {convergence.facebook_targeting && (
                    <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-transparent to-indigo-50/30" />
                        <div className="relative p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg">
                                    <Facebook className="h-5 w-5 text-blue-600" />
                                </div>
                                <h2 className="text-xl font-bold text-gray-900">Facebook Targeting</h2>
                            </div>
                            <pre className="whitespace-pre-wrap text-sm bg-white/40 p-4 rounded-lg border border-white/50">
                                {JSON.stringify(convergence.facebook_targeting, null, 2)}
                            </pre>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2">
                {isAdmin && (
                    <Dialog>
                        <DialogTrigger asChild>
                            <Button variant="destructive" className="flex items-center gap-2">
                                <ShieldAlert className="h-4 w-4" />
                                Raw convergence data [SUPERADMIN ONLY]
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[800px] max-h-[80vh]">
                            <DialogHeader className="flex flex-row items-center justify-between pr-0">
                                <DialogTitle>Raw Convergence Data</DialogTitle>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={copyToClipboard} 
                                    className="flex items-center gap-2"
                                >
                                    {isCopied ? (
                                        <><Check className="h-4 w-4" /> Copied</>
                                    ) : (
                                        <><Clipboard className="h-4 w-4" /> Copy to clipboard</>
                                    )}
                                </Button>
                            </DialogHeader>
                            <ScrollArea className="h-[60vh] w-full rounded-md p-4 bg-slate-50">
                                <pre className="whitespace-pre-wrap font-mono text-sm">
                                    {formatRawData(convergence)}
                                </pre>
                            </ScrollArea>
                        </DialogContent>
                    </Dialog>
                    )}
                </div>
            </div>
        </div>
    );
};
